import useTranslation from '../../hooks/useTranslation';
import ExportProgressList from '../ExportProgressList';
import OrgFieldPicker from '../OrgFieldPicker';

import { VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import Banner from 'components/Banner';
import Link from 'components/Link';
import SourceBadge from 'components/SourceBadge';

import { TEST_ID } from './constants';
import type { FunctionComponent, ModalState, Props, ReactElement } from './types';

import styles from './styles.module.scss';

const JohnDeereExportModalBody: FunctionComponent<Props> = ({
    fields,
    isFieldsLoading,
    isOrganizationsLoading,
    onFieldSelect,
    onOrganizationSelect,
    organizations,
    selectedFieldId,
    selectedOrganizationId,
    siteName,
    state,
    testId = TEST_ID
}) => {
    const t = useTranslation();

    const bodyByKind: Record<ModalState['kind'], () => ReactElement> = {
        autoResolved: () => (
            <VerticalContainer className={styles.johnDeereExportModalBody} testId={testId}>
                <Banner icon='info' text={t.AUTO_RESOLVED_DESCRIPTION} variant='info' />

                <SourceBadge name={siteName} />

                <Wrapper className={styles.fieldName}>
                    {state.kind === 'autoResolved' ? state.fieldName : ''}
                </Wrapper>
            </VerticalContainer>
        ),
        error: () => (
            <VerticalContainer className={styles.johnDeereExportModalBody} testId={testId}>
                <Banner
                    icon='warning'
                    metadata={state.kind === 'error' ? state.message : undefined}
                    text={t.ERROR_TITLE}
                    variant='warn'
                />
            </VerticalContainer>
        ),
        exporting: () => (
            <ExportProgressList currentStep={state.kind === 'exporting' ? state.step : 1} />
        ),
        needsPicker: () => (
            <VerticalContainer className={styles.johnDeereExportModalBody} testId={testId}>
                <Banner icon='info' text={t.PICKER_DESCRIPTION} variant='info' />

                <OrgFieldPicker
                    fields={fields}
                    isFieldsLoading={isFieldsLoading}
                    isOrganizationsLoading={isOrganizationsLoading}
                    onFieldSelect={onFieldSelect}
                    onOrganizationSelect={onOrganizationSelect}
                    organizations={organizations}
                    selectedFieldId={selectedFieldId}
                    selectedOrganizationId={selectedOrganizationId}
                />
            </VerticalContainer>
        ),
        resolving: () => <Banner icon='info' testId={testId} text={t.RESOLVING} variant='info' />,
        success: () => (
            <VerticalContainer className={styles.johnDeereExportModalBody} testId={testId}>
                <Banner
                    icon='info'
                    metadata={t.SUCCESS_DESCRIPTION}
                    text={t.SUCCESS_TITLE}
                    variant='context'
                />

                {state.kind === 'success' && state.viewUrl ? (
                    <Link
                        className={styles.viewLink}
                        target='_blank'
                        text={t.SUCCESS_VIEW_LINK}
                        to={state.viewUrl}
                    />
                ) : null}
            </VerticalContainer>
        )
    };

    return bodyByKind[state.kind]();
};

export default JohnDeereExportModalBody;
